import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [state, setState] = useState({ phase: "checking" });
  const { clear } = useCart();
  const tries = useRef(0);

  useEffect(() => {
    if (!sid) return setState({ phase: "error" });
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") { if (!data.gift_card) clear(); return setState({ phase: "paid", ...data }); }
        if (data.payment_status === "expired") return setState({ phase: "error" });
      } catch { /* retry */ }
      if (++tries.current < 15) timer = setTimeout(poll, 2000);
      else setState({ phase: "error" });
    };
    poll();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sid]);

  return (
    <div className="max-w-2xl mx-auto px-4 py-24 text-center" data-testid="payment-success-page">
      {state.phase === "checking" && <><Loader2 className="w-10 h-10 mx-auto animate-spin text-[#8C2220]" /><p className="mt-4" data-testid="payment-checking">Confirming your payment…</p></>}
      {state.phase === "paid" && state.gift_card && (
        <div className="rise" data-testid="giftcard-success">
          <CheckCircle2 className="w-14 h-14 mx-auto text-emerald-700" />
          <h1 className="mt-6 font-serif text-4xl font-bold">Your gift card is ready!</h1>
          <p className="mt-4 text-slate-600 max-w-md mx-auto">{state.send_date ? `We'll email the £${Number(state.amount).toFixed(2)} gift card to ${state.recipient} on the morning of ${new Date(state.send_date).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}.` : `We've emailed the £${Number(state.amount).toFixed(2)} gift card to ${state.recipient}.`} Your receipt, with a copy of the gift card number, has gone to {state.email}.</p>
          <Link to="/" className="mt-8 inline-block rounded-full bg-[#8C2220] text-white px-7 py-3.5 font-semibold">Back to the shop</Link>
        </div>
      )}
      {state.phase === "paid" && !state.gift_card && (
        <div className="rise">
          <CheckCircle2 className="w-14 h-14 mx-auto text-emerald-700" />
          <h1 className="mt-6 font-serif text-4xl font-bold">Thank you for your order!</h1>
          <p className="mt-4 text-slate-600">Your order number is</p>
          <p className="mt-1 font-mono text-2xl font-bold text-[#8C2220]" data-testid="order-number">{state.order_number}</p>
          <p className="mt-6 text-slate-600 max-w-md mx-auto">We've emailed a confirmation to {state.email}. Our shopper will now buy your items and post them with Evri.</p>
          <Link to={`/track?order=${state.order_number}`} className="mt-8 inline-block rounded-full bg-[#8C2220] text-white px-7 py-3.5 font-semibold" data-testid="track-this-order">Track this order</Link>
        </div>
      )}
      {state.phase === "error" && (
        <><XCircle className="w-12 h-12 mx-auto text-rose-700" /><p className="mt-4" data-testid="payment-error">We couldn't confirm your payment. If you were charged, please check your email or track your order.</p><Link to="/" className="mt-6 inline-block underline">Back to shop</Link></>
      )}
    </div>
  );
}
